import seoTable from './publicPages.json';
import { alternatesOf, livePages, type PublicPage, type SeoData } from './prerender';

const defaultData = seoTable as unknown as SeoData;

/** The live page at this address. A held (switched off) Arabic page does not exist as far as the site is concerned. */
const pageAt = (pathname: string, data: SeoData): PublicPage | undefined => {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return livePages(data).find((p) => p.path === clean);
};

/** Whether the Arabic pages are published (the "arabic" switch in publicPages.json). */
export const arabicIsLive = (data: SeoData = defaultData): boolean => data.arabic === true;

/** The language of the page at this address: 'ar' for a live Arabic public page, English for everything else. */
export function langOfPath(pathname: string, data: SeoData = defaultData): 'en' | 'ar' {
  return pageAt(pathname, data)?.lang === 'ar' ? 'ar' : 'en';
}

export const dirOfLang = (lang: 'en' | 'ar'): 'rtl' | 'ltr' => (lang === 'ar' ? 'rtl' : 'ltr');

/** The other-language version of this page, if there is one and it is live (for the language switch). */
export function otherLanguageOf(pathname: string, data: SeoData = defaultData): { lang: 'en' | 'ar'; path: string } | null {
  const page = pageAt(pathname, data);
  if (!page) return null;
  if (page.translationOf) return { lang: 'en', path: page.translationOf };
  const translation = livePages(data).find((p) => p.translationOf === page.path);
  return translation ? { lang: translation.lang ?? 'en', path: translation.path } : null;
}

/** hreflang entries for this address, empty when the page has no live translation. */
export function alternatesForPath(pathname: string, data: SeoData = defaultData) {
  const page = pageAt(pathname, data);
  return page ? alternatesOf(page, data) : [];
}
